import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faTimesCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Slide, Snackbar } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import useResponsive from '../hooks/useResponsive';

const SeverityIcons = new Map([
    ['success', faCheckCircle],
    ['error', faTimesCircle],
    ['warning', faExclamationTriangle],
    ['info', faInfoCircle],
]);

const StyledAlert = styled(Alert)(({ theme }) => {
    return {
        borderRadius: theme.spacing(1.5),
        boxShadow: theme.shadows[2],
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        alignItems: 'center',
        backgroundColor: theme.palette.common.white,
        '& .MuiAlert-icon': {
            padding: 0,
            marginRight: theme.spacing(1),
        },
        '& .MuiAlert-message': {
            padding: 0,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            lineHeight: theme.typography.subtitle1.lineHeight,
        },
    };
});

const DefaultDuration = 3000;

export interface SnackbarProps {
    message: string;
    severity?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    preventClickAway?: boolean;
}

interface SnackbarContextType {
    showSnackbar: (
        message: string,
        severity?: 'success' | 'error' | 'warning' | 'info',
        duration?: number,
        preventClickAway?: boolean
    ) => void;
    showSuccess: (message: string, duration?: number, preventClickAway?: boolean) => void;
    showError: (message: string, duration?: number, preventClickAway?: boolean) => void;
    showWarning: (message: string, duration?: number, preventClickAway?: boolean) => void;
    showInfo: (message: string, duration?: number, preventClickAway?: boolean) => void;
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
    children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
    const [snackbar, setSnackbar] = useState<SnackbarProps | null>(null);
    const [open, setOpen] = useState(false);
    const { palette, spacing, zIndex } = useTheme();
    const { isMobile } = useResponsive();

    const showSnackbar = useCallback(
        (
            message: string,
            severity: 'success' | 'error' | 'warning' | 'info' = 'info',
            duration: number = DefaultDuration,
            preventClickAway: boolean = false
        ) => {
            setSnackbar({ message, severity, duration, preventClickAway });
            setOpen(true);
        },
        []
    );

    const showSuccess = useCallback(
        (
            message: string,
            duration: number = DefaultDuration,
            preventClickAway: boolean = false
        ) => {
            showSnackbar(message, 'success', duration, preventClickAway);
        },
        [showSnackbar]
    );

    const showError = useCallback(
        (
            message: string,
            duration: number = DefaultDuration,
            preventClickAway: boolean = false
        ) => {
            showSnackbar(message, 'error', duration, preventClickAway);
        },
        [showSnackbar]
    );

    const showWarning = useCallback(
        (
            message: string,
            duration: number = DefaultDuration,
            preventClickAway: boolean = false
        ) => {
            showSnackbar(message, 'warning', duration, preventClickAway);
        },
        [showSnackbar]
    );

    const showInfo = useCallback(
        (
            message: string,
            duration: number = DefaultDuration,
            preventClickAway: boolean = false
        ) => {
            showSnackbar(message, 'info', duration, preventClickAway);
        },
        [showSnackbar]
    );

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        // If preventClickAway is true, only allow auto-hide (reason === 'timeout')
        if (snackbar?.preventClickAway && reason !== 'timeout') {
            return;
        }
        setOpen(false);
    };

    const contextValue: SnackbarContextType = useMemo(
        () => ({
            showSnackbar,
            showSuccess,
            showError,
            showWarning,
            showInfo,
        }),
        [showError, showInfo, showSuccess, showWarning, showSnackbar]
    );

    return (
        <SnackbarContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={snackbar?.duration}
                onClose={handleClose}
                anchorOrigin={
                    isMobile
                        ? { vertical: 'top', horizontal: 'center' }
                        : { vertical: 'top', horizontal: 'right' }
                }
                sx={{
                    zIndex: zIndex.snackbar,
                    '&.MuiSnackbar-anchorOriginTopCenter': { top: spacing(2.5) },
                }}
                slots={{
                    transition: Slide,
                }}
            >
                {snackbar ? (
                    <StyledAlert
                        severity={snackbar.severity || 'info'}
                        variant="filled"
                        icon={
                            <FontAwesomeIcon
                                icon={
                                    SeverityIcons.get(snackbar.severity || 'info') || faInfoCircle
                                }
                                size="sm"
                                color={palette[snackbar.severity || 'info'].main}
                            />
                        }
                        sx={{
                            width: isMobile ? `calc(100% - ${spacing(2.5)})` : undefined,
                            color: 'common.black',
                        }}
                    >
                        {snackbar.message}
                    </StyledAlert>
                ) : undefined}
            </Snackbar>
        </SnackbarContext.Provider>
    );
}

export function useSnackbar(): SnackbarContextType {
    const context = useContext(SnackbarContext);
    if (context === undefined) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
}
